import { Link } from "react-router-dom";
import { LegalLayout, Section, Table, List } from "./LegalLayout";
import { COMPANY, privacyContact } from "@/config/company";

/**
 * Informativa sull'uso dell'intelligenza artificiale.
 *
 * Assolve a tre obblighi distinti:
 *  - art. 50 Reg. UE 2024/1689 (AI Act), applicabile dal 2 agosto 2026:
 *    trasparenza su sistemi che interagiscono con le persone, che generano
 *    contenuti sintetici e di categorizzazione biometrica;
 *  - art. 4 AI Act: alfabetizzazione in materia di IA (già in vigore);
 *  - L. 132/2025: obbligo per imprese e professionisti di dichiarare in modo
 *    chiaro l'impiego di strumenti di IA nelle proprie prestazioni.
 *
 * ⚠️ La tabella dei sistemi va tenuta allineata alle funzioni realmente attive.
 * Se attivi o disattivi una funzione IA, aggiorna questa pagina.
 */
export default function AiPage() {
  return (
    <LegalLayout
      title="Uso dell'intelligenza artificiale"
      subtitle="Dove AURA usa l'intelligenza artificiale, cosa fa esattamente, quali sono i limiti e chi decide."
    >
      <Section n={1} title="Perché trovi questa pagina">
        <p>
          Il Regolamento europeo sull'intelligenza artificiale (Reg. UE 2024/1689, “AI Act”)
          impone di dire chiaramente quando e come si usa l'IA. Gli obblighi di trasparenza
          dell'art. 50 si applicano <strong>dal 2 agosto 2026</strong>. La legge italiana
          132/2025, in vigore dal 10 ottobre 2025, aggiunge per imprese e professionisti il
          dovere di dichiarare l'impiego di strumenti di IA nelle proprie prestazioni.
        </p>
        <p>
          Questa pagina è la nostra dichiarazione. È scritta per essere capita: se qualcosa
          non ti è chiaro, scrivici a{" "}
          <a href={`mailto:${privacyContact()}`} className="underline underline-offset-4">
            {privacyContact()}
          </a>
          .
        </p>
      </Section>

      <Section n={2} title="Il principio che seguiamo">
        <p>
          <strong>L'IA in AURA suggerisce, non decide.</strong> Ogni funzione assistita da
          intelligenza artificiale produce una proposta che l'operatrice può accettare,
          modificare o ignorare. Nessuna decisione che riguarda una cliente — un trattamento,
          un prezzo, un rifiuto — viene presa automaticamente dal software.
        </p>
        <p>
          Questo non è solo un principio etico: è ciò che tiene la piattaforma fuori dalla
          categoria dei sistemi ad alto rischio e ciò che l'art. 14 dell'AI Act chiama
          sorveglianza umana significativa.
        </p>
      </Section>

      <Section n={3} title="Le funzioni che usano l'IA">
        <Table
          head={["Funzione", "Cosa fa", "Cosa NON fa", "Obbligo applicabile"]}
          rows={[
            [
              "Analisi della pelle",
              "Da una fotografia del viso stima parametri come tono, idratazione e imperfezioni, e propone trattamenti dal catalogo del centro. Con l'invio all'AI esterna attivo, la foto va a Google Gemini; altrimenti la stima è fatta sul computer del centro.",
              "Non formula diagnosi mediche, non rileva patologie, non identifica la persona e non la confronta con altre.",
              "Art. 50.3 AI Act: la persona ripresa va informata prima. Art. 9 GDPR: serve consenso esplicito (foto e analisi AI).",
            ],
            [
              "Prodotti consigliati",
              "In base a tipo di pelle e allergie dichiarate propone prodotti del catalogo del centro e segnala quelli da evitare. Il gestionale ricontrolla gli allergeni: un prodotto con un'allergia dichiarata non compare mai tra i consigliati.",
              "Non riceve nome, contatti, terapie o note della cliente e non sostituisce la verifica dell'INCI sull'etichetta.",
              "Art. 9 GDPR: consenso esplicito della cliente all'analisi AI.",
            ],
            [
              "Tono delle note e commento sul trend della pelle",
              "Classifica il livello di soddisfazione espresso nelle note scritte dallo staff e commenta l'andamento dei parametri della pelle. Di base il calcolo avviene sul computer del centro; Gemini solo su richiesta.",
              "Non analizza volto o voce per dedurre emozioni; le note con dati sanitari non vengono inviate.",
              "Art. 9 GDPR se l'invio all'AI esterna è attivo: consenso della cliente.",
            ],
            [
              "Assistente vocale e chat",
              "Risponde alle domande dell'operatrice su agenda, magazzino e clienti ed esegue comandi (nuovo appuntamento, nota). La voce è trascritta da ElevenLabs o Google; al modello l'agenda arriva con codici al posto dei nomi.",
              "Non decide al posto dell'operatrice e non invia nomi e cognomi delle clienti presenti in agenda.",
              "Art. 50.1 AI Act: chi interagisce sa che parla con un sistema di IA.",
            ],
            [
              "Lettura delle bolle di consegna",
              "Riconosce con l'OCR prodotti, lotti e quantità dai documenti di trasporto e propone l'aggiornamento del magazzino.",
              "Non conferma il carico da sola: la registrazione va confermata dall'operatrice.",
              "Nessun obbligo specifico: non tratta dati personali né genera contenuti.",
            ],
            [
              "Anteprima del colore su unghie",
              "Genera un'immagine che simula lo smalto scelto sulle mani della cliente.",
              "Non è una fotografia reale e non rappresenta il risultato garantito del trattamento.",
              "Art. 50.2 AI Act: l'immagine è contenuto sintetico e va marcata come tale.",
            ],
            [
              "Testi per newsletter e campagne",
              "Propone bozze di testo per comunicazioni commerciali del centro.",
              "Non invia nulla in automatico: l'operatrice rilegge, corregge e approva.",
              "Art. 50.2 AI Act sui testi generati; art. 130 D.Lgs 196/2003 sui consensi marketing.",
            ],
            [
              "Briefing giornaliero e indicatori",
              "Riassume appuntamenti, incassi attesi e scorte in esaurimento; evidenzia andamenti.",
              "Non valuta le persone e non produce classifiche automatiche a fini disciplinari.",
              "Se usata su dati del personale: obblighi informativi verso i lavoratori (punto 7).",
            ],
          ]}
        />
      </Section>

      <Section n={4} title="Quali fornitori di IA e chi li sceglie">
        <p>
          Le funzioni di IA del gestionale usano <strong>Google Gemini</strong> (Google LLC /
          Google Ireland Ltd) e, per la voce, <strong>ElevenLabs</strong>. I dati partono dal
          computer del centro con la chiave API del centro: è il centro, come titolare, a
          scegliere il fornitore e ad accettarne i termini (per Gemini con chiave a pagamento,
          così i contenuti non vengono usati per migliorare i prodotti di Google). Il
          trattamento può avvenire fuori dall'Unione Europea, con le garanzie previste dal
          fornitore (EU-U.S. Data Privacy Framework o clausole contrattuali tipo).
        </p>
        <p>
          L'invio dei dati delle clienti all'IA esterna è <strong>disattivato di
          default</strong>: il centro lo attiva nelle impostazioni e, anche dopo, i dati di una
          cliente partono solo se lei ha dato il consenso all'analisi con IA e solo quando
          l'operatrice lo chiede espressamente. Nome, cognome, telefono ed email non vengono
          mai inviati.
        </p>
      </Section>

      <Section n={5} title="Contenuti generati dall'intelligenza artificiale">
        <p>
          Le immagini di anteprima del colore e i testi proposti per le comunicazioni sono{" "}
          <strong>contenuti sintetici</strong>: prodotti da un sistema di IA, non da una
          persona e non da una fotocamera.
        </p>
        <p>
          All'interno della piattaforma questi contenuti sono sempre etichettati in modo
          visibile. Ai sensi dell'art. 50.2 dell'AI Act, come modificato dal Regolamento UE
          2026/1744, entro il <strong>2 dicembre 2026</strong> l'etichettatura sarà
          integrata anche in formato leggibile dalle macchine, mediante marcatura nei
          metadati dei file generati.
        </p>
        <p>
          Chiediamo ai centri che utilizzano questi contenuti verso il pubblico di mantenere
          l'indicazione della loro origine artificiale: è un obbligo che ricade su chi
          diffonde il contenuto.
        </p>
      </Section>

      <Section n={6} title="Analisi della pelle: cosa deve sapere la cliente">
        <p>
          È la funzione più delicata, perché parte da un'immagine del volto. Regole che
          applichiamo e che i centri devono rispettare:
        </p>
        <List
          items={[
            "la cliente va informata prima della ripresa che verrà usato un sistema di intelligenza artificiale, e deve poter dire di no senza conseguenze sul trattamento richiesto;",
            "serve un consenso esplicito e separato, perché si tratta di dati riconducibili alla salute (art. 9 GDPR): nel gestionale sono i consensi \"Foto/immagini\" e \"Analisi AI\", senza i quali la fotocamera resta bloccata e nulla parte verso l'IA esterna;",
            "il risultato è un'indicazione estetica, non un parere medico (il gestionale lo scrive su ogni esito): in presenza di lesioni, nei sospetti o in caso di dubbio va indirizzata a un dermatologo;",
            "le fotografie restano nell'archivio del centro e non sono condivise con altri centri; quando l'analisi usa Gemini la foto viene inviata a Google solo per elaborarla e, con chiave a pagamento, non viene usata per addestrare modelli;",
            "la cliente può chiedere in ogni momento la cancellazione delle immagini e delle analisi collegate;",
            "l'esito proposto dal sistema è sempre rivisto dall'operatrice prima di essere presentato.",
          ]}
        />
        <p>
          Al centro forniamo il testo informativo pronto da mostrare e il modulo di consenso
          dedicato. Il dettaglio sul trattamento dei dati è nell'{" "}
          <Link to="/privacy" className="underline underline-offset-4">
            informativa privacy
          </Link>
          .
        </p>
      </Section>

      <Section n={7} title="Se l'IA tocca il personale del centro">
        <p>
          Alcuni indicatori della dashboard elaborano dati riferibili alle singole
          operatrici (appuntamenti gestiti, incassi, obiettivi). Quando un datore di lavoro
          usa strumenti di questo tipo, la legge italiana richiede adempimenti precisi che
          restano a carico del centro:
        </p>
        <List
          items={[
            "informativa scritta ai lavoratori sull'uso di sistemi decisionali o di monitoraggio automatizzati (art. 1-bis D.Lgs 152/1997);",
            "comunicazione chiara di finalità, logiche di funzionamento e limiti dei sistemi di IA impiegati (L. 132/2025);",
            "se lo strumento consente un controllo a distanza dell'attività lavorativa, accordo sindacale o autorizzazione dell'Ispettorato del lavoro (art. 4 Statuto dei Lavoratori);",
            "divieto di decisioni automatiche su valutazione, disciplina o cessazione del rapporto senza intervento umano.",
          ]}
        />
        <p>
          Mettiamo a disposizione dei centri un modello di informativa al personale. Resta
          responsabilità del centro adottarlo e, dove serve, attivare le procedure sindacali.
        </p>
      </Section>

      <Section n={8} title="Limiti che dichiariamo apertamente">
        <List
          items={[
            "i sistemi di IA possono sbagliare: le stime sulla pelle variano con luce, trucco e qualità della fotocamera;",
            "l'OCR può leggere male un documento sbiadito o scritto a mano;",
            "i testi generati possono contenere affermazioni imprecise e vanno sempre riletti;",
            "le anteprime di colore sono simulazioni: la resa reale dipende da prodotto, supporto e illuminazione;",
            "nessuna funzione è pensata per finalità mediche, diagnostiche o di sicurezza.",
          ]}
        />
      </Section>

      <Section n={9} title="Cosa NON facciamo">
        <List
          items={[
            "non usiamo riconoscimento facciale per identificare le persone;",
            "non deduciamo emozioni o stati d'animo da volto, voce o altri dati biometrici, né origine etnica, orientamento sessuale, convinzioni religiose o politiche (il \"tono delle note\" riguarda solo il testo scritto dallo staff sulla soddisfazione per il servizio);",
            "non attribuiamo punteggi sociali alle persone;",
            "non addestriamo modelli sui dati delle clienti dei centri;",
            "non prendiamo decisioni automatizzate con effetti giuridici sulle persone.",
          ]}
        />
        <p>
          Sono pratiche vietate dall'art. 5 dell'AI Act o comunque escluse per scelta.
        </p>
      </Section>

      <Section n={10} title="Formazione e competenze">
        <p>
          L'art. 4 dell'AI Act, in vigore dal 2 febbraio 2025, richiede che chi sviluppa e
          chi utilizza sistemi di IA abbia un livello adeguato di competenza. {COMPANY.brand}{" "}
          forma il proprio personale sul funzionamento e sui limiti dei sistemi impiegati e
          fornisce ai centri clienti materiale formativo e istruzioni d'uso, così che le
          operatrici sappiano cosa il sistema fa, cosa non può fare e quando non fidarsi del
          risultato.
        </p>
      </Section>

      <Section n={11} title="Segnalazioni">
        <p>
          Se pensi che una funzione di IA abbia prodotto un risultato sbagliato o dannoso,
          segnalacelo a{" "}
          <a href={`mailto:${privacyContact()}`} className="underline underline-offset-4">
            {privacyContact()}
          </a>
          . Registriamo ogni segnalazione, rispondiamo entro 30 giorni e, se il problema è
          confermato, correggiamo la funzione informando i centri interessati.
        </p>
      </Section>
    </LegalLayout>
  );
}
